import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  }
}));

const CommissionEditPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    partnerId: '',
    partnerName: '',
    tripId: '',
    tripReference: '',
    bookingId: '',
    bookingReference: '',
    amount: '',
    currency: 'EUR',
    status: 'pending',
    description: '',
  });
  
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCommission();
  }, [id]);

  const fetchCommission = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const commissionDoc = doc(firestore, 'commissions', id);
      const commissionSnapshot = await getDoc(commissionDoc);
      
      if (commissionSnapshot.exists()) {
        const commissionData = {
          id: commissionSnapshot.id,
          ...commissionSnapshot.data()
        };
        setFormData(commissionData);
        setOriginalData(commissionData);
      } else {
        // Si la commission n'existe pas, utiliser des données de démonstration
        if (id === 'COM001') {
          const demoCommission = { 
            id: 'COM001', 
            partnerId: 'P001',
            partnerName: 'Entreprise ABC',
            tripId: 'T001',
            tripReference: 'VOY-2025-001',
            bookingId: 'B001',
            bookingReference: 'RES-2025-001',
            amount: 45.50,
            currency: 'EUR',
            status: 'pending',
            description: 'Commission pour trajet Paris-Lyon',
            createdAt: '2025-03-15T10:30:00',
            updatedAt: '2025-03-15T10:30:00',
            paymentDate: null
          };
          setFormData(demoCommission);
          setOriginalData(demoCommission);
        } else {
          setSnackbar({
            open: true,
            message: 'Commission non trouvée',
            severity: 'error'
          });
          setTimeout(() => {
            navigate('/admin/commissions');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error fetching commission:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de la commission',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleCancel = () => {
    navigate(`/admin/commissions/${id}`);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.amount) {
      newErrors.amount = 'Le montant est requis';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Le montant doit être un nombre positif';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        severity: 'error'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Préparer les données pour l'envoi
      const commissionData = {
        ...formData,
        amount: parseFloat(formData.amount),
        updatedAt: new Date().toISOString()
      };
      
      // Si le statut a changé à "paid", ajouter la date de paiement
      if (formData.status === 'paid' && originalData.status !== 'paid') {
        commissionData.paymentDate = new Date().toISOString();
      }
      
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const commissionDoc = doc(firestore, 'commissions', id);
      await updateDoc(commissionDoc, commissionData);
      
      setSnackbar({
        open: true,
        message: 'Commission mise à jour avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la page de détail après un court délai
      setTimeout(() => {
        navigate(`/admin/commissions/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error updating commission:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour de la commission',
        severity: 'error'
      });
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour aux détails
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Modifier la commission
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations de base
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="partnerName"
                  label="Partenaire"
                  variant="outlined"
                  fullWidth
                  value={formData.partnerName}
                  disabled
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                  >
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="paid">Payée</MenuItem>
                    <MenuItem value="cancelled">Annulée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider className={classes.divider} />
            
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails du voyage et de la réservation
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="tripReference"
                  label="Référence du voyage"
                  variant="outlined"
                  fullWidth
                  value={formData.tripReference}
                  disabled
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="bookingReference"
                  label="Référence de la réservation"
                  variant="outlined"
                  fullWidth
                  value={formData.bookingReference}
                  disabled
                />
              </Grid>
            </Grid>
            
            <Divider className={classes.divider} />
            
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails financiers
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="amount"
                  label="Montant"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0.01,
                    step: 0.01
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="currency-label">Devise</InputLabel>
                  <Select
                    labelId="currency-label"
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    label="Devise"
                  >
                    <MenuItem value="EUR">Euro (€)</MenuItem>
                    <MenuItem value="USD">Dollar américain ($)</MenuItem>
                    <MenuItem value="GBP">Livre sterling (£)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={3}
                />
              </Grid>
              
              {formData.status === 'paid' && (
                <Grid item xs={12}>
                  <TextField
                    name="paymentReference"
                    label="Référence de paiement (optionnel)"
                    variant="outlined"
                    fullWidth
                    value={formData.paymentReference || ''}
                    onChange={handleChange}
                    placeholder="Ex: VIREMENT-2025-042"
                  />
                </Grid>
              )}
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={24} /> : <SaveIcon />}
              >
                {submitting ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionEditPage;
